import { useEffect, useRef, useState } from 'react';

import { useAui } from '@assistant-ui/react';

import { useReturnThread } from '@/hooks/use-return-thread';
import { useAssistantCloudStore } from '@/lib/assistant-cloud-local/stores/assistant-cloud-store';
import { useHistorySession } from '@/lib/history-key/session';
import { suppressSwitchBroadcast } from '@/lib/hitl/resumable-storage';
import { findThread } from '@/lib/utils/thread';

export function useHistoryLock({
  hasStorageAccess = true,
  storageChecked = true,
  enabled = true,
} = {}): void {
  const api = useAui();
  const owner = useHistorySession(state => state.owner);
  const lockedOwner = useHistorySession(state => state.lockedOwner);
  const previous = useRef({ owner, access: storageChecked ? hasStorageAccess : null, lost: false });
  const [attempt, setAttempt] = useState({ owner, access: hasStorageAccess, count: 0 });
  // The conversation open when history locked for an account that may yet be confirmed.
  const resume = useRef<{ owner: string; threadId: string } | undefined>(undefined);

  useEffect(
    function forgetResumeOnceSignedOut() {
      if (!owner && !lockedOwner) resume.current = undefined;
    },
    [owner, lockedOwner],
  );

  useEffect(() => {
    const changed = owner !== previous.current.owner;
    if (changed) {
      const left = previous.current.owner;
      if (owner === null && left !== null && useHistorySession.getState().lockedOwner === left) {
        resume.current = { owner: left, threadId: openThreadId(api) };
      } else if (owner !== null && resume.current?.owner !== owner) {
        resume.current = undefined;
      }
    }
    const storageChanged =
      previous.current.access !== null && hasStorageAccess !== previous.current.access;
    // A failed reopen keeps its sign-out pending for the retry, but a sign-in since then is its own
    // change: it must not leave the conversation whose call it resumes.
    const lostAccount =
      (previous.current.lost && !changed) ||
      (changed && previous.current.owner !== null) ||
      (storageChanged && !hasStorageAccess);
    previous.current = {
      owner,
      access: storageChecked ? hasStorageAccess : null,
      lost: lostAccount,
    };
    if (!storageChecked || !enabled) return;
    const count =
      !changed && !storageChanged && attempt.owner === owner && attempt.access === hasStorageAccess
        ? attempt.count
        : 0;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    function isCurrent() {
      return !cancelled && useHistorySession.getState().owner === owner;
    }
    function cancel() {
      cancelled = true;
      clearTimeout(timer);
    }
    function returnIfConfirmed() {
      const pending = resume.current?.owner === owner ? resume.current : null;
      if (!pending) return;
      resume.current = undefined;
      // Without storage nothing is readable to go back to, and thread persistence isn't mounted.
      if (hasStorageAccess) useReturnThread.setState({ threadId: pending.threadId });
    }
    const store = useAssistantCloudStore.getState();
    if (
      !count &&
      !lostAccount &&
      !storageChanged &&
      (hasStorageAccess ? owner === store.openedFor : true)
    ) {
      // Confirmed before the lock got as far as reopening the history: nothing to reopen.
      returnIfConfirmed();
      return cancel;
    }

    async function reopen() {
      // Let an existing load settle before resetting its state.
      await store.initPromise?.catch(() => undefined);
      if (!isCurrent()) return;
      // A suspension, not a sign-out: other tabs must not follow this one to a blank conversation,
      // since its return to the open one is never broadcast.
      if (lostAccount && resume.current) suppressSwitchBroadcast();
      if (await reopenHistory({ api, lostAccount, isCurrent })) {
        previous.current.lost = false;
        if (count) setAttempt({ owner, access: hasStorageAccess, count: 0 });
        returnIfConfirmed();
      }
    }
    function retry() {
      setAttempt({ owner, access: hasStorageAccess, count: count + 1 });
    }
    void reopen().catch(function retryFailedReopen(error: unknown) {
      if (!isCurrent()) return;
      console.error('Failed to reopen conversation history', error);
      if (count < 2) timer = setTimeout(retry, 250 * 4 ** count);
    });
    return cancel;
  }, [owner, api, hasStorageAccess, storageChecked, enabled, attempt]);
}

function openThreadId(api: ReturnType<typeof useAui>): string {
  const { mainThreadId, threadItems } = api.threads.getState();
  return findThread(Object.values(threadItems), mainThreadId)?.remoteId ?? mainThreadId;
}

/** Detach hidden runtimes: reloading the list alone retains their decrypted messages. */
export async function reopenHistory({
  api,
  lostAccount,
  isCurrent,
}: {
  api: ReturnType<typeof useAui>;
  lostAccount: boolean;
  isCurrent: () => boolean;
}) {
  const draft = api.composer.getState().text;
  const store = useAssistantCloudStore.getState();
  // Signed out or switched account: no earlier conversation stays selected, so none can linger
  // under a stale id once the list reloads.
  if (lostAccount) {
    await api.threads.switchToNewThread();
    if (!isCurrent()) return false;
    if (draft) api.composer.setText(draft);
  }
  await store.reset();
  if (!isCurrent()) return false;
  try {
    // Without storage this loads an empty store, and the reload is what takes this page's
    // conversations off the list: detaching them only stops their runtimes.
    await store.initialize();
    if (!isCurrent()) return false;
    await api.threads.reload();
  } finally {
    if (isCurrent()) {
      await detachHiddenHistory({ api, alreadyNew: lostAccount, draft, isCurrent });
    }
  }
  return isCurrent();
}

async function detachHiddenHistory({
  api,
  alreadyNew,
  draft,
  isCurrent,
}: {
  api: ReturnType<typeof useAui>;
  alreadyNew: boolean;
  draft: string;
  isCurrent: () => boolean;
}) {
  const readable = useAssistantCloudStore.getState().threads;
  const hidden = Object.values(api.threads.getState().threadItems).filter(
    item => item.status !== 'new' && !readable.has(item.remoteId ?? item.id),
  );
  const state = api.threads.getState();
  if (
    !alreadyNew &&
    state.mainThreadId &&
    (!findThread(Object.values(state.threadItems), state.mainThreadId) ||
      hidden.some(item => item.id === state.mainThreadId))
  ) {
    await api.threads.switchToNewThread();
    if (!isCurrent()) return false;
    if (draft) api.composer.setText(draft);
  }
  for (const item of hidden) {
    await api.threads.item({ id: item.id }).detach();
    if (!isCurrent()) return false;
  }
  return true;
}
